import React from "react";
import { LOGO_BLUE, ACCOMPANIMENT_IMG } from "../lib/constants";
import { ArrowRight, RotateCcw } from "lucide-react";
import { useLang } from "../lib/i18n";
import { LanguageToggle } from "./CommunitySelection";

export const WelcomeScreen = ({ onBegin, loading, onContribute, resumeAvailable, onResume }) => {
  const { t } = useLang();
  return (
    <div className="min-h-screen flex flex-col bg-[var(--page)]">
      <div className="relative flex-1 flex items-center justify-center overflow-hidden">
        <div className="absolute top-4 right-4 z-20">
          <LanguageToggle variant="light" />
        </div>
        <div className="relative z-10 w-full max-w-2xl mx-auto px-6 py-16 text-center">
          <img
            src={LOGO_BLUE}
            alt="Root Causes Challenge"
            data-testid="welcome-logo"
            className="w-64 sm:w-80 mx-auto mb-6 animate-rise"
          />
          <p className="text-xs uppercase tracking-[0.24em] font-semibold text-vin/80 mb-5 leading-relaxed">
            {t("An Interactive Poverty Simulation")}
          </p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-ink leading-tight">
            {t("Understand today. Create a more just tomorrow.")}
          </h1>
          <p className="mt-5 text-lg text-ink-soft font-body">
            {t("Walk in the shoes of a neighbor experiencing poverty.")}
          </p>
          <p className="mt-6 text-base text-ink-soft leading-relaxed max-w-xl mx-auto">
            {t("A formation experience in empathy and civic understanding. This is not a game to win, and not about saving money. It is an invitation to understand how poverty limits choices and forces impossible trade-offs — so we can listen, learn, and act with greater understanding.")}
          </p>

          <figure className="mt-10 mx-auto max-w-lg animate-rise">
            <div className="rounded-xl overflow-hidden border border-[var(--line)] shadow-xl bg-white">
              <img
                src={ACCOMPANIMENT_IMG}
                alt="Two neighbors walking together along the same road, one carrying his shoes"
                data-testid="accompaniment-image"
                className="w-full h-auto object-cover"
              />
            </div>
            <figcaption
              data-testid="accompaniment-quote"
              className="mt-5 font-hand text-3xl sm:text-4xl leading-snug text-vin-dark px-3"
            >
              &ldquo;{t("We cannot understand the choices poverty forces until we are willing to walk for a while on the same road")}&rdquo;
            </figcaption>
          </figure>

          <button
            data-testid="begin-button"
            onClick={onBegin}
            disabled={loading}
            className="mt-10 inline-flex items-center gap-2 bg-vin text-white font-semibold px-8 py-4 rounded-full hover:bg-vin-hover transition-colors duration-200 disabled:opacity-60"
          >
            {loading ? t("Loading…") : resumeAvailable ? t("Start a new walk-through") : t("Begin")}
            <ArrowRight className="w-5 h-5" strokeWidth={1.75} />
          </button>
          {resumeAvailable && (
            <div className="mt-4">
              <button
                data-testid="resume-button"
                onClick={onResume}
                disabled={loading}
                className="inline-flex items-center gap-2 text-vin font-semibold text-sm px-6 py-3 rounded-full border border-vin/40 hover:bg-vin/5 transition-colors duration-200 disabled:opacity-60"
              >
                <RotateCcw className="w-4 h-4" strokeWidth={1.75} />
                {t("Resume your walk-through")}
              </button>
              <p className="mt-2 text-[11px] text-ink-soft">{t("You have an unfinished walk-through on this device.")}</p>
            </div>
          )}
          <p className="mt-8 text-xs text-ink-soft">
            {t("About 10–15 minutes · No sign-in required · Fully anonymous")}
          </p>
          <div className="mt-6 pt-6 border-t border-[var(--line)] flex flex-col items-center gap-3">
            <button
              data-testid="welcome-contribute-button"
              onClick={onContribute}
              className="text-sm text-vin font-semibold underline underline-offset-4 hover:text-vin-hover transition-colors duration-200"
            >
              {t("Or contribute your own community →")}
            </button>
            <a
              href="/lessons"
              data-testid="welcome-lessons-link"
              className="text-sm text-vin font-semibold underline underline-offset-4 hover:text-vin-hover transition-colors duration-200"
            >
              {t("Read the Lessons Learned wall →")}
            </a>
            <a
              href="/voices"
              data-testid="welcome-voices-link"
              className="text-sm text-vin font-semibold underline underline-offset-4 hover:text-vin-hover transition-colors duration-200"
            >
              {t("Policy Pathways → From Empathy to Action")}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeScreen;
